// The `query` source op — the generic, provider-routed dataset source.
//
// `(query "Name" (filter #(…)) (columns [:a :b]) (limit n) (sort …))`
//
// A special form: its leading positional is the dataset name and every
// trailing form is an op-call-shaped constraint read STRUCTURALLY (never
// dispatched as an op). The op resolves the owning provider in the
// knowledge-graph group by first-`claims(name)`, runs the shared
// plan-time fold (`parseSourcePipeline`) to split the constraints into the
// provider's absorbed `native` blob plus the residual ops that replay
// client-side, asks the provider to mint the `ProviderSource` via its SSOT
// `buildSource`, and emits a `LazyDataset` carrying the source + residual
// ops. No I/O happens here — the provider's `fetch` runs later, at
// materialisation time, dispatched by `providerId` through the registry on
// the `EvalContext`.
//
// A leaf provider with no `isPushable` (e.g. the in-memory provider)
// pushes nothing: the whole predicate, sort, columns, and limit fall to
// the generic residual stage and replay client-side — the source op stays
// correct regardless of how much the backend can absorb.

import { z } from 'zod';

import type { SourcePos } from '../ast.ts';
import type { EvalResult } from '../env.ts';
import { FacadeSchemaError, UnknownDatasetError } from '../errors.ts';
import { type AstInput, astShape, extractLambdaBody, opCallPos } from '../normalize.ts';
import { type ColumnRefInArg, opMeta, type SourceSchemaCtx, type SpecialCtx } from '../op-meta.ts';
import { columnNameAstList } from '../ops/transform/helpers.ts';
import { paramPath } from '../path.ts';
import type { LogicalPlan } from '../plan.ts';
import { astArg } from '../schema/ast-codec.ts';
import { exprColRefs } from '../schema/check.ts';
import { type Schema, schemaRefines } from '../schema/types.ts';
import {
  type Dataset,
  expectDataset,
  expectMaterialized,
  isLazy,
  LazyDataset,
  type MaterializedDataset,
  promoteSchema,
  type Value,
} from '../value.ts';
import { decodePipeline } from './decode.ts';
import { KNOWLEDGE_GRAPH_GROUP, SOURCE_OP_NAMES, TIMESERIES_GROUP } from './groups.ts';
import { type PipelineHooks, parseSourcePipeline } from './pipeline.ts';
import { datapointsRowsSource, rowsOuterSchema, selectSeriesRefs } from './timeseries.ts';
import type { DatasetProvider, DecodedParams, FacadeConsumable, PlanCtx } from './types.ts';

/**
 * The pipeline hooks for a leaf provider that pushes nothing down — the
 * shape every provider without `isPushable` / `translate` / native
 * constraint absorption uses (the in-memory provider in this phase). Every
 * predicate leaf is non-pushable, so the whole filter (and sort / columns
 * / limit) falls to the generic residual stage and replays client-side.
 */
const NON_PUSHING_HOOKS: PipelineHooks<AstInput> = {
  isPushable: () => false,
  // `translate` is never reached when nothing is pushable, but the
  // partition contract requires a function; identity is the safe no-op.
  translate: (node) => node,
  absorbableConstraints: [],
};
/**
 * The default set of params a facade consumes when its `rewrite` returns
 * no explicit `consumes` list: just the predicate it branched on. Every
 * other present constraint (`sort` / `limit` / `columns`) then composes
 * onto the expansion as a residual op.
 */
const DEFAULT_FACADE_CONSUMES: readonly FacadeConsumable[] = ['filter'];

/**
 * The constraint kind a trailing pipeline form represents, keyed off its
 * head op name, or `undefined` for a non-constraint form. Used to decide
 * which original forms a facade consumed (and so must be dropped) versus
 * which compose onto the expansion as residual ops.
 */
const formConstraintKind = (form: AstInput): FacadeConsumable | undefined => {
  const shape = astShape(form);
  if (shape.kind !== 'opCall') {
    return undefined;
  }
  const { name } = shape.call;
  return name === 'filter' || name === 'sort' || name === 'limit' || name === 'columns' ? name : undefined;
};

/**
 * Compose the constraint forms a facade did NOT consume onto its
 * expansion, preserving referential transparency: threading
 * `(sort …)` / `(limit …)` / `(columns …)` after the bare expansion is
 * exactly what `(query "F" … (sort …))` must equal. Each unconsumed form
 * is applied left-to-right by splicing the running dataset value into the
 * op call's first position (the thread-first shape) and evaluating it
 * through the engine — reusing the real transform ops, no bespoke replay.
 */
const composeResidualForms = async (
  expansion: Dataset,
  forms: readonly AstInput[],
  consumed: ReadonlySet<FacadeConsumable>,
  evalNode: (node: AstInput) => Promise<Value>,
  pos: SourcePos,
): Promise<Dataset> => {
  const residualForms = forms.filter((form) => {
    const kind = formConstraintKind(form);
    return kind !== undefined && !consumed.has(kind);
  });
  // A `Dataset` is a valid `AstInput` literal, so it splices directly into
  // the op call's first position without a type assertion; each transform
  // op returns a `Dataset` again, so the fold stays well-typed.
  let acc: Dataset = expansion;
  for (const form of residualForms) {
    const shape = astShape(form);
    // `formConstraintKind` filtered to op-call forms only; re-narrow so the
    // threaded reconstruction stays free of a type assertion.
    if (shape.kind !== 'opCall') {
      continue;
    }
    const threaded: AstInput = {
      ...shape.call,
      params: [acc, ...(shape.call.params ?? [])],
    };
    acc = expectDataset(await evalNode(threaded), 'facade residual op', pos);
  }
  return acc;
};

/**
 * Collect the column refs a `query` op's trailing forms reference, with a
 * precise per-ref AST path so an unknown-column diagnostic lands on the
 * offending token. Walks each trailing form positionally (forms start at
 * `params[1]`, after the leading name at `params[0]`):
 *
 *  - `(filter <lambda>)` → the refs inside the lambda body (same walk
 *    `filter`'s own `referencedColumns` does), rooted at the body's path.
 *  - `(columns [:a :b])` → one ref per projected name, at the element path.
 *  - `(sort …)` → the refs inside the sort key forms.
 *
 * Returns an empty list for an unrecognised / refless form. Generalises
 * the single-`:filter`/`:columns` enumeration the view-query op uses to N
 * positional trailing forms. `formStart` is the param index the trailing
 * forms begin at: `1` for `query` (after its leading name positional), `0`
 * for `datapoints` (no leading name) — so each ref's diagnostic path lands
 * on the right token regardless of the leading-positional count.
 *
 * `skipFilter` excludes the `(filter …)` form from the walk. A view query's
 * filter is a row predicate over the declared schema, so its columns are
 * validated here. A `datapoints` `(filter …)` is instead a SERIES SELECTION
 * over series-identity / metadata attributes (`externalId`, `space`,
 * `isString`, …) — a namespace broader than the row-per-series outer schema
 * the static pass validates against. Validating it here would spuriously
 * reject a legitimate non-identity selection attribute; the selection is
 * fully checked at apply time (the bounded-selection guard), so the static
 * walk skips it and only reports the genuine outer-row constraints
 * (`(columns …)` / `(sort …)`).
 */
const queryReferencedColumns = (
  data: { readonly params?: readonly AstInput[] },
  basePath: string,
  formStart = 1,
  skipFilter = false,
): readonly ColumnRefInArg[] => {
  const forms = (data.params ?? []).slice(formStart);
  return forms.flatMap((form, i): readonly ColumnRefInArg[] => {
    const formPath = paramPath(basePath, i + formStart);
    const shape = astShape(form as AstInput);
    if (shape.kind !== 'opCall') {
      return [];
    }
    const { name, params = [] } = shape.call;
    if (name === 'filter') {
      if (skipFilter) {
        return [];
      }
      const extracted = extractLambdaBody(params[0]);
      if (extracted === undefined) {
        return [];
      }
      const [body, lambdaToBody] = extracted;
      // A nested `(query …)` / `(datapoints …)` in the predicate opens its
      // own row scope, so its column refs are validated against the inner
      // dataset, not this query's schema — stop the walk at that boundary.
      return exprColRefs(body, `${paramPath(formPath, 0)}/${lambdaToBody}`, SOURCE_OP_NAMES);
    }
    if (name === 'columns') {
      return columnNameAstList(params[0]).map((colName, j) => ({
        name: colName,
        path: `${paramPath(formPath, 0)}/${j}`,
      }));
    }
    if (name === 'sort') {
      return params.flatMap((key, j) => exprColRefs(key, paramPath(formPath, j), SOURCE_OP_NAMES));
    }
    return [];
  });
};

/**
 * Build the op-apply `PlanCtx` — the full-context eager seam a provider's
 * `rewrite` (facade) and `plan` (pushdown leaf) share. Carries exactly
 * what nested resolution + facade expansion need: the bindings, a
 * sub-node evaluator, an inner-query materialiser, the provider resolver,
 * the abort signal, and the source position for diagnostics. `label`
 * scopes the materialiser's expect-dataset diagnostic to the caller's
 * phase. The narrow fetch seam (`SourceFetcherCtx`) never receives this.
 */
const buildPlanCtx = (ctx: SpecialCtx, label: string, pos: SourcePos): PlanCtx => ({
  bindings: ctx.evalCtx.bindings,
  evalNode: (node: AstInput): Promise<Value> => ctx.evalNode(node, ctx.env, ctx.path).then((r) => r.value),
  materialize: (ds: Dataset): Promise<MaterializedDataset> =>
    isLazy(ds) ? ctx.materialize(ds) : Promise.resolve(expectMaterialized(ds, label, pos)),
  getProvider: (group, datasetName) => ctx.evalCtx.getProvider?.(group, datasetName),
  signal: ctx.evalCtx.signal,
  pos,
});

/**
 * Run a facade provider's rewrite path and compose the result.
 *
 *  1. expand the query via `provider.rewrite(decoded, planCtx)`;
 *  2. evaluate the expansion through the engine (recursion — the
 *     expansion is a normal expression, type-checked on evaluation);
 *  3. assert the expansion's result schema refines the facade's declared
 *     schema at the boundary, so the facade keeps its promised shape
 *     (`FacadeSchemaError` on mismatch — a programming error);
 *  4. compose the constraint forms the facade did NOT consume onto the
 *     expansion as residual ops.
 *
 * The whole expand-evaluate-compose is memoized on
 * `(providerId, decoded)` via the conversation session cache, so an
 * identical facade query does not re-rewrite or re-plan within a turn.
 */
const facadeExpand = async (
  provider: DatasetProvider,
  rewrite: NonNullable<DatasetProvider['rewrite']>,
  name: string,
  decoded: DecodedParams,
  forms: readonly AstInput[],
  ctx: SpecialCtx,
  pos: SourcePos,
): Promise<Value> => {
  const evalNode = (node: AstInput): Promise<Value> => ctx.evalNode(node, ctx.env, ctx.path).then((r) => r.value);
  return ctx.evalCtx.session.fetch(['facade-expansion', provider.id, name, decoded], async () => {
    const planCtx = buildPlanCtx(ctx, 'facade expansion', pos);
    const { expansion, consumes } = rewrite(decoded, planCtx);

    const expanded = await evalNode(expansion);
    const expandedDs = expectDataset(expanded, 'facade expansion', pos);
    const declared = provider.schema(name, { name });
    if (!schemaRefines(expandedDs.schema, declared)) {
      throw new FacadeSchemaError(name, [...declared.keys()], [...expandedDs.schema.keys()], pos);
    }

    // Promote the facade's declared schema onto the expansion. The expansion's
    // statically-propagated schema is a conservative LOWER bound — a no-unit,
    // discriminator-less TS-ref query leaves measure columns `timeseries<unknown>`
    // — whereas `declared` carries the promoted units from `resolveColumnUnit`.
    // The `schemaRefines` check above guarantees the two are compatible; the
    // shared `promoteSchema` primitive lifts every column to its most-concrete
    // type (units merging evidence-first, so a cell-derived unit on the
    // expansion outranks the declaration where both exist). Residual ops below
    // then propagate the unit-bearing types forward.
    const promoted = promoteSchema(expandedDs, declared);

    const consumed = new Set(consumes ?? DEFAULT_FACADE_CONSUMES);
    return composeResidualForms(promoted, forms, consumed, evalNode, pos);
  });
};

/**
 * `query` — the generic provider-routed dataset source op. Leading name +
 * trailing constraint pipeline forms; resolves the provider claiming the
 * name in the knowledge-graph group and defers the fetch to materialise
 * time.
 */
export const query = z
  .object({
    name: z.literal('query'),
    params: z.tuple([z.string()]).rest(astArg('<pipeline-form>')),
  })
  .describe(
    'Query a registered dataset by name. Returns a `LazyDataset` materialised on demand. ' +
      'Positional: the dataset name. Trailing pipeline forms constrain the query — ' +
      '`(filter #(predicate-body))` (row columns via `(:col %)`), ' +
      '`(columns [:a :b])` (project to these columns), `(limit n)`, `(sort [:col])`. ' +
      'Whatever the dataset backend cannot push down runs client-side automatically — ' +
      'the result is identical regardless, so constraints can always be written inside the query.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'io',
    // Static-check contract: resolve the provider's declared schema (sync,
    // no I/O) so the pre-evaluation pass validates the trailing forms'
    // column refs against it. The provider registry reaches this pass via
    // `SourceSchemaCtx.getProvider` — the same registry the runtime
    // `EvalContext` carries, so a dataset name resolves identically at
    // type-check and at materialise time.
    dataset: {
      sourceSchema: (data, ctx: SourceSchemaCtx): Schema | undefined => {
        const name = data.params[0];
        const provider = ctx.getProvider?.(KNOWLEDGE_GRAPH_GROUP, name);
        return provider?.schema(name, { name });
      },
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => queryReferencedColumns(data, basePath),
    },
    example: [
      {
        expr: '(query "WellAsset" (filter #(= (:type %) "production")) (columns [:externalId :name :type]) (limit 100))',
      },
    ],
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const pos = opCallPos(ctx.spans, ctx.path);
      const name = data.params[0];

      // Resolve the owning provider in the knowledge-graph group by
      // first-`claims`. No provider claims the name → a precise
      // unknown-dataset diagnostic. A provider that claims the name but is
      // neither a facade (`rewrite`) nor a leaf (it mints a source via its
      // own `plan` or the SSOT `buildSource`) is a wiring bug and surfaces
      // the same way.
      const provider = ctx.evalCtx.getProvider?.(KNOWLEDGE_GRAPH_GROUP, name);
      const buildSource = provider?.buildSource;
      if (
        provider === undefined ||
        (provider.rewrite === undefined && provider.plan === undefined && buildSource === undefined)
      ) {
        throw new UnknownDatasetError(name, [], pos);
      }

      // Decode the trailing constraint forms once. Cross-dataset set refs and
      // inline `(query …)` sub-query operands in the predicate were already
      // collapsed to literals by `inlineSetRefs` (run per-form in `evalScript`
      // before this op is dispatched), so `decoded` carries no dataset-valued
      // node into partition, the plan, or any cache key.
      const forms = (data.params as readonly AstInput[]).slice(1);
      const decoded = decodePipeline(forms);

      // Facade path takes precedence: a provider with `rewrite` expands the
      // query into an expression the engine re-evaluates, then composes the
      // constraints the facade did not consume as residual ops on top of
      // the expansion. The facade owns no source — it reuses the entire
      // engine via its expansion.
      if (provider.rewrite !== undefined) {
        const value = await facadeExpand(provider, provider.rewrite, name, decoded, forms, ctx, pos);
        return { value, env: ctx.env };
      }

      // Pushdown leaf path: a provider with its own eager `plan` resolves
      // the per-query context it needs (a CDF view), partitions the
      // literalised predicate into the legal absorbed prefix + residual ops,
      // and mints the `ProviderSource` directly — so the pushed filter,
      // columns, and limit fold into one `native` blob in a single pass.
      // The engine still owns the boolean split + ordering legality via the
      // shared partitioner the provider's `plan` drives.
      if (provider.plan !== undefined) {
        const planCtx = buildPlanCtx(ctx, 'query pushdown', pos);
        const { source, residual, schema } = provider.plan(name, decoded, planCtx);
        const plan: LogicalPlan = { source, ops: residual };
        const lazyDs = new LazyDataset(plan, schema);
        return { value: lazyDs satisfies Value, env: ctx.env };
      }

      // Non-pushing leaf path: reaching here without `buildSource` was
      // rejected above. Run the shared plan-time fold over the literalised
      // constraints: partition the predicate into the provider's `native`
      // filter + a residual predicate, gate sort / limit / columns through
      // the ordering-legality planner, and assemble the non-absorbed
      // constraints into ordered residual ops. A non-pushing leaf provider
      // pushes nothing, so every constraint becomes a residual op.
      if (buildSource === undefined) {
        throw new UnknownDatasetError(name, [], pos);
      }
      const inputSchema = provider.schema(name, { name });
      const parsed = parseSourcePipeline(decoded, inputSchema, NON_PUSHING_HOOKS);

      // Mint the provider's `ProviderSource` via its SSOT plan
      // constructor; emit a `LazyDataset` carrying it + the residual ops.
      // The declared schema is the fold's post-projection schema, so the
      // size-strict `schemaRefines` post-condition holds after fetch +
      // residual replay.
      const { source } = buildSource({ name });
      const plan: LogicalPlan = { source, ops: parsed.residualOps };
      const lazyDs = new LazyDataset(plan, parsed.schema);
      return { value: lazyDs satisfies Value, env: ctx.env };
    },
  });

/**
 * `datapoints` — the timeseries source op. Parameterless: series
 * selection is an absorbed `externalId` / `space` `=` / `in` filter, not a
 * leading dataset name. Returns a row-per-series `LazyDataset` (one row
 * per selected series, each carrying a nested `datapoints` cell) resolved
 * on demand through the timeseries provider.
 */
export const datapoints = z
  .object({
    name: z.literal('datapoints'),
    params: z.tuple([]).rest(astArg('<pipeline-form>')),
  })
  .describe(
    'Read timeseries datapoints, one row per selected series with a nested `datapoints` cell ' +
      '(`{timestamp, value, isString}`). Series selection is REQUIRED and bounds the read: ' +
      '`(datapoints (filter #(in (:externalId %) ["id-a" "id-b"])))` or `#(= (:externalId %) "id-a")`. ' +
      'Trailing pipeline forms constrain the result (`(sort …)`, `(limit n)`, `(columns […])`). ' +
      'An unbounded read (no `externalId`/`space` selection) is rejected.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'io',
    // Static-check contract: the timeseries provider's declared schema is
    // the row-per-series outer shape, resolved synchronously via the
    // registry — so a trailing `(columns …)` / `(sort …)` column ref is
    // validated against `{externalId, space, datapoints}` before any fetch.
    // The `(filter …)` selection is NOT validated here: it selects over
    // series-identity / metadata attributes (a broader namespace than the
    // outer row schema) and is fully checked at apply time by the
    // bounded-selection guard.
    dataset: {
      sourceSchema: (_data, ctx: SourceSchemaCtx): Schema | undefined => {
        const provider = ctx.getProvider?.(TIMESERIES_GROUP, 'datapoints');
        return provider?.schema('datapoints', { ref: { externalId: '' } });
      },
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] =>
        // `datapoints` has no leading name positional — its trailing forms
        // start at `params[0]`, so the column-ref walk starts at form index
        // 0. The selection `(filter …)` is skipped (apply-time validated).
        queryReferencedColumns({ params: data.params as readonly AstInput[] }, basePath, 0, true),
    },
    example: [
      {
        expr: '(datapoints (filter #(in (:externalId %) ["pump-1" "pump-2"])) (limit 100))',
      },
    ],
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const pos = opCallPos(ctx.spans, ctx.path);

      // Resolve the timeseries provider (a leaf) in the timeseries group.
      // No provider claims `datapoints` → a precise unknown-dataset
      // diagnostic; a claimer that is not a leaf is a wiring bug, same.
      const provider = ctx.evalCtx.getProvider?.(TIMESERIES_GROUP, 'datapoints');
      if (provider === undefined || provider.buildSource === undefined) {
        throw new UnknownDatasetError('datapoints', [], pos);
      }

      // Decode the trailing constraint forms once. A dataset-valued sub-query
      // in the selection predicate (e.g. `(in (:externalId %) (query "Pumps"))`)
      // was already materialised + collapsed to an inlined literal by
      // `inlineSetRefs` (run per-form in `evalScript` before this op is
      // dispatched), so the selection here is over literals.
      const forms = data.params as readonly AstInput[];
      const decoded = decodePipeline(forms);

      // Bounded-selection guard: extract the `externalId` / `space`
      // selection (rejects an unbounded read or an over-cap selection),
      // then mint the row-per-series source.
      const refs = selectSeriesRefs(decoded, pos);
      const source = datapointsRowsSource(provider.id, refs);

      // Residual pipeline: the row-per-series outer schema is the planner's
      // input. The selection filter replays harmlessly over the already-
      // selected rows (every row matches it), and sort / limit / columns
      // replay client-side. A non-pushing leaf pushes nothing, so every
      // constraint becomes a residual op.
      const parsed = parseSourcePipeline(decoded, rowsOuterSchema(), NON_PUSHING_HOOKS);
      const plan: LogicalPlan = { source, ops: parsed.residualOps };
      const lazyDs = new LazyDataset(plan, parsed.schema);
      return { value: lazyDs satisfies Value, env: ctx.env };
    },
  });

/**
 * The provider-framework source ops, composed into the canonical op
 * surface: `query` (view-style datasets) and `datapoints` (timeseries).
 */
export const providerOps = [query, datapoints] as const;
